/* eslint-disable react/no-array-index-key */
import { toast } from 'react-toastify';
import { CircularProgress, DialogTitle, Divider, Grid2 as Grid, Paper, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v1 as uuidV1 } from 'uuid';
import { debounce } from 'lodash';
import { CUSTOM_EXPRESSION_FUNCTION_TABLE, defaultSearchOptions } from '../constants/ClientData';
import { ClientDataBranch } from '../constants/ClientDataBranch';
import { DOCUMENTATION_COLUMN, KEY_COLUMN } from '../constants/ClientDataColumn';
import { ClientDataFixedColumns } from '../constants/ClientDataFixedColumns';
import { ClientDataType } from '../constants/ClientDataType';
import { unknownGroup } from '../constants/Group';
import { I18nKeys } from '../constants/I18nKeys';
import {
  setCreateCustomExpressionFunctionExpression,
  setCreateCustomExpressionFunctionOpen,
  setSearchValue as setSearchValueFunc,
  fetchSearchResults as fetchSearchResultsFunc,
  replaceValues as replaceValuesAction,
  setSearchOptions,
  setTablesToIgnore,
} from '../ducks/clientDataSlice';
import { useAppDispatch, useAppSelector } from '../hooks';
import { clientDataApi } from '../services/clientDataApi';
import { AppState } from '../types/AppState';
import { LoadingButton } from './LoadingButton';
import { GridData } from '../types/DataGrid';
import { getVendorFromClientId, mapClientAndDataTypeAndTableToUndoStackId } from '../utils/clientIdUtils';
import { ClientDataSearchFindAll } from './ClientDataSearchFindAll';
import { getClientSearchData, getMaxFindAllSectionHeight, replaceValues } from '../utils/searchUtils';
import { useClientDataRepo } from '../hooks/useClientDataRepo';
import { useFunLibrary } from '../hooks/useFunLibrary';

const CAMEL_CASE_REGEX = /^[a-z]+[a-zA-Z0-9]*$/;

const useStyles = makeStyles(() => ({
  root: {
    padding: '8px',
  },
  fields: {
    display: 'block',
    padding: '0px 24px',
  },
  field: {
    marginBottom: '15px',
  },
  buttons: {
    marginTop: '15px',
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '0px 24px',
  },
  button: {
    marginLeft: '8px',
  },
  progress: {
    width: '100%',
    textAlign: 'center',
  },
  rowsAffectedText: {
    marginTop: '8px',
    marginBottom: '15px',
  },
  noRowsAffectedText: {
    textAlign: 'center',
  },
}));

interface Props {
  gridRef: React.RefObject<AgGridReact>;
}

export const ClientDataCreateCustomExpressionFunction: React.FC<Props> = ({ gridRef }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const initialFunctionExpression = useAppSelector(
    (state: AppState) => state?.clientData.createCustomExpressionFunction.expression,
  );

  const isSearchLoading = useAppSelector((state: AppState) => state?.clientData.search.result.loading);

  const { data: funLibData } = useFunLibrary();

  const functionKeysInUse = useMemo(
    () =>
      [funLibData[ClientDataType.Reference], funLibData[ClientDataType.Supplier]]
        .flat()
        .map(({ [KEY_COLUMN]: k }) => k)
        .filter((k) => !!k)
        .reduce((acc, k) => acc.add(k), new Set()),
    [funLibData],
  );

  const [functionKey, setFunctionKey] = useState('');
  const functionKeyFieldErrors = () => {
    const errors = [];
    if (!functionKey) errors.push('Cannot be blank.');
    if (functionKey && !CAMEL_CASE_REGEX.test(functionKey)) errors.push('Key must follow the camel case convetion.');
    if (functionKeysInUse.has(functionKey)) errors.push('This key is already in use.');
    return errors.length ? (
      <>
        {errors.map((err, i) => (
          <React.Fragment key={`key-error-${i}`}>
            {err}
            <br />
          </React.Fragment>
        ))}
      </>
    ) : null;
  };

  const [functionExpression, setFunctionExpression] = useState(initialFunctionExpression?.trim());
  const functionExpressionFieldErrors = () => {
    const errors = [];
    if (!functionExpression) errors.push('Cannot be blank.');
    if (functionExpression.includes('fun.')) errors.push('Cannot call another fun function.');
    return errors.length ? (
      <>
        {errors.map((err, i) => (
          <React.Fragment key={`key-error-${i}`}>
            {err}
            <br />
          </React.Fragment>
        ))}
      </>
    ) : null;
  };

  const [functionDocumentation, setFunctionDocumentation] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchData, setSearchData] = useState<{ [vendor: string]: GridData }>({});

  const close = () => dispatch(setCreateCustomExpressionFunctionOpen(false));

  const { user, group: { groupId } = unknownGroup } = useAppSelector((state: AppState) => state?.currentUser);

  const setSearchDataFunc = useCallback((data: { [id: string]: GridData }) => {
    setSearchData(
      Object.entries(data).reduce(
        (vendorSearchData, [id, clientSearchData]) => ({
          ...vendorSearchData,
          [getVendorFromClientId(id)]: clientSearchData,
        }),
        {} as { [vendor: string]: GridData },
      ),
    );
  }, []);

  const { api: gridApi, columnApi: gridColApi } = gridRef?.current || {};
  gridApi?.stopEditing(true); // Close any grid editors

  const fetchSearchResults = useCallback(
    () =>
      dispatch(fetchSearchResultsFunc({ onSearchComplete: setSearchDataFunc, gridApi, gridColApi, resetSearch: true })),
    [dispatch, gridApi, gridColApi, setSearchDataFunc],
  );

  useEffect(() => {
    dispatch(setTablesToIgnore([CUSTOM_EXPRESSION_FUNCTION_TABLE]));
    dispatch(setSearchOptions(defaultSearchOptions));
    dispatch(setSearchValueFunc(initialFunctionExpression));
    fetchSearchResults();
  }, [dispatch, fetchSearchResults, initialFunctionExpression]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce((searchString: string) => {
      dispatch(setSearchValueFunc(searchString.trim()));
      fetchSearchResults();
    }, 300),
    [dispatch, fetchSearchResults],
  );

  useEffect(
    () => () => {
      dispatch(setTablesToIgnore([]));
      dispatch(setSearchValueFunc(''));
      dispatch(setCreateCustomExpressionFunctionExpression(''));
      debouncedSearchHandler.cancel();
    },
    [debouncedSearchHandler, dispatch],
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const findAllSectionRef = useRef<HTMLDivElement>(null);

  const {
    clientId,
    clientDataType,
    clientDataBranch = ClientDataBranch.Main,
    selectedTable = '',
  } = useAppSelector((state) => state?.clientData);

  const selectedViewerId = useAppSelector(
    (state: AppState) => state?.viewer?.selectedTabId || state?.viewer?.selectedClientId,
  );
  const clientDataId = mapClientAndDataTypeAndTableToUndoStackId(selectedViewerId, clientDataType, selectedTable);

  const { cellMetadata } = useClientDataRepo({
    useClientTables: true,
    useCellMetadata: true,
  });

  const replaceParams = {
    searchData: getClientSearchData(searchData, clientId),
    searchValue: functionExpression,
    replaceValue: `fun.${functionKey}()`,
    searchOptions: defaultSearchOptions,
    onSearchComplete: setSearchDataFunc,
    cellMetadata,
    selectedTable: '',
    dispatch,
    gridApi,
    gridColApi,
  };

  const create = async () => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    try {
      await dispatch(
        clientDataApi.endpoints.updateClientData.initiate({
          clientId,
          groupId,
          dataType: ClientDataType.Supplier,
          branch: clientDataBranch,
          data: {
            [CUSTOM_EXPRESSION_FUNCTION_TABLE]: [
              {
                [ClientDataFixedColumns.RowId]: uuidV1(),
                [KEY_COLUMN]: functionKey,
                [DOCUMENTATION_COLUMN]: functionDocumentation,
                expression: functionExpression,
              },
            ],
          },
          metadata: null,
          message: `Create custom expression function: ${functionKey}`,
          newBranch: false,
          user,
        }),
      );

      const onReplace = (resultIdsToReplace: string[]): void => {
        replaceValues(clientDataId, replaceParams, resultIdsToReplace, true);
        close();
      };
      await dispatch(replaceValuesAction({ params: replaceParams, onReplace, replaceAll: true }));
    } finally {
      setIsProcessing(false);
    }
  };

  if (clientDataType !== ClientDataType.Supplier) {
    toast.error(`Extract to function is not available on the ${clientDataType} dataset`);
    close();
    return <div />;
  }

  if (clientDataBranch === ClientDataBranch.Main) {
    toast.error(`Extract to function is not available for the ${clientDataBranch} branch`);
    close();
    return <div />;
  }

  return (
    <Paper ref={rootRef} className={classes.root}>
      <DialogTitle id="create-custom-expression-function-title">
        {t(I18nKeys.ClientDataCreateCustomExpressionFunctionTitle, {
          defaultValue: 'Create Custom Expression Function',
        })}
      </DialogTitle>
      <Grid container className={classes.fields}>
        <TextField
          id="create-custom-expression-function-key-field"
          autoFocus
          className={classes.field}
          required
          fullWidth
          label={t(I18nKeys.ClientDataCreateCustomExpressionFunctionKeyLabel, { defaultValue: 'Key' })}
          value={functionKey}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setFunctionKey(e.target.value)}
          type="text"
          variant="filled"
          error={!!functionKeyFieldErrors()}
          helperText={functionKeyFieldErrors()}
        />
        <TextField
          id="create-custom-expression-function-expression-field"
          className={classes.field}
          required
          multiline
          rows={Math.max(functionExpression.split('\n').length, 4)}
          fullWidth
          label={t(I18nKeys.ClientDataCreateCustomExpressionFunctionExpressionLabel, { defaultValue: 'Expression' })}
          value={functionExpression}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setFunctionExpression(e.target.value);
            debouncedSearchHandler(e.target.value);
          }}
          type="text"
          variant="filled"
          error={!!functionExpressionFieldErrors()}
          helperText={functionExpressionFieldErrors()}
        />
        <TextField
          id="create-custom-expression-function-documentation-field"
          className={classes.field}
          multiline
          rows={4}
          fullWidth
          label={t(I18nKeys.ClientDataCreateCustomExpressionFunctionDocumentationLabel, {
            defaultValue: 'Documentation',
          })}
          value={functionDocumentation}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setFunctionDocumentation(e.target.value)}
          type="text"
          variant="filled"
        />
        <Divider />
        <Typography className={classes.rowsAffectedText} variant="subtitle1">
          {t(I18nKeys.ClientDataCreateCustomExpressionFunctionRowsAffected, {
            defaultValue: 'Rows that will be affected:',
          })}
        </Typography>
        <Grid
          ref={findAllSectionRef}
          size={{ xs: 12 }}
          style={{
            overflowY: 'auto',
            maxHeight: getMaxFindAllSectionHeight({ searchRootRef: rootRef, findAllSectionRef }),
          }}
        >
          {isSearchLoading && (
            <div className={classes.progress}>
              <CircularProgress size={24} color="primary" />
            </div>
          )}

          {!isSearchLoading && Object.keys(searchData).some((key) => Object.keys(searchData[key]).length > 0) && (
            <ClientDataSearchFindAll
              refs={{
                search: rootRef,
                findAll: findAllSectionRef,
                rootGrid: gridRef,
              }}
              searchData={searchData}
            />
          )}

          {!isSearchLoading &&
            (Object.keys(searchData).length === 0 ||
              Object.keys(searchData).every((key) => Object.keys(searchData[key]).length === 0)) && (
              <Typography className={classes.noRowsAffectedText} variant="body1">
                {t(I18nKeys.ClientDataCreateCustomExpressionFunctionNoRowsAffected, {
                  defaultValue: 'Zero rows will be affected.',
                })}
              </Typography>
            )}
        </Grid>
      </Grid>
      <Grid className={classes.buttons}>
        <LoadingButton
          id="create-custom-expression-function-close-button"
          className={classes.button}
          color="primary"
          loading={isProcessing}
          onClick={() => close()}
        >
          {t(I18nKeys.ClientDataCreateCustomExpressionFunctionCancelButton, { defaultValue: 'Close' })}
        </LoadingButton>
        <LoadingButton
          id="create-custom-expression-function-create-button"
          className={classes.button}
          color="primary"
          loading={isProcessing}
          disabled={!!functionKeyFieldErrors() || !!functionExpressionFieldErrors()}
          onClick={() => create()}
        >
          {t(I18nKeys.ClientDataCreateCustomExpressionFunctionCreateButton, { defaultValue: 'Create' })}
        </LoadingButton>
      </Grid>
    </Paper>
  );
};
